/**
 * Self-drawing pipeline graphic: source, target and release stage.
 */

import React, { useEffect, useRef } from "react";
import { t } from "../i18n";

export function _prepLine(line) {
  const len = Math.ceil(line.getTotalLength());
  line.style.setProperty("--len", len);
  line.style.strokeDasharray = len;
  line.style.strokeDashoffset = len;
}

function isReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function playPipeline(svgRoot, opts = {}) {
  if (!svgRoot) return;
  const lineDraw = opts.lineDraw ?? 300;
  const lines = [...svgRoot.querySelectorAll(".pl-line")];
  const nodes = [...svgRoot.querySelectorAll(".pl-node")];
  const chevs = [...svgRoot.querySelectorAll(".pl-chev")];
  lines.forEach(_prepLine);
  if (isReducedMotion()) {
    lines.forEach((l) => l.classList.add("draw"));
    nodes.forEach((n) => n.classList.add("pop"));
    chevs.forEach((c) => c.classList.add("show"));
    return;
  }
  const seq = [];
  let cursor = 120;
  if (nodes[0]) seq.push([cursor, () => nodes[0].classList.add("pop")]);
  lines.forEach((ln, i) => {
    cursor += 160;
    seq.push([cursor, () => ln.classList.add("draw")]);
    if (chevs[i]) seq.push([cursor + 90, () => chevs[i].classList.add("show")]);
    cursor += lineDraw;
    if (nodes[i + 1]) seq.push([cursor, () => nodes[i + 1].classList.add("pop")]);
  });
  svgRoot._plTimers = seq.map(([d, fn]) => setTimeout(fn, d));
}

export function resetPipeline(svgRoot) {
  if (!svgRoot) return;
  (svgRoot._plTimers || []).forEach(clearTimeout);
  svgRoot._plTimers = [];
  svgRoot.querySelectorAll(".pl-line").forEach((l) => l.classList.remove("draw"));
  svgRoot.querySelectorAll(".pl-node").forEach((n) => n.classList.remove("pop"));
  svgRoot.querySelectorAll(".pl-chev").forEach((c) => c.classList.remove("show"));
}

/**
 * Renders the pipeline graphic shown in the detail panel.
 *
 * @param {{code: string, name: string, glyph?: string}[]} stages glyph is a
 *   sprite symbol id, e.g. "ic-stage-dev"
 * @param {number} tgtIdx index of the target stage, usually length - 2
 */
export default function PipelineSvg({ stages, tgtIdx, autoPlay = true }) {
  const rootRef = useRef(null);

  useEffect(() => {
    if (!autoPlay) return;
    const svg = rootRef.current;
    if (!svg) return;
    // Two rAF ticks: the first lets node layout settle (getTotalLength needs
    // final geometry), the second starts the self-draw animation from the
    // full path length.
    let raf1, raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => playPipeline(svg));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      resetPipeline(svg);
    };
  }, [autoPlay, stages, tgtIdx]);

  if (!stages || stages.length === 0) return null;

  const W = 560, H = 118, cy = 50, r = 20, m = 70;
  const gap = stages.length > 1 ? (W - 2 * m) / (stages.length - 1) : 0;
  const xs = stages.map((_, i) => m + i * gap);

  return (
    <svg
      ref={rootRef}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${W} ${H}`}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label="Transport-Pipeline"
    >
      {stages.slice(0, -1).map((_, i) => {
        const x1 = xs[i] + r + 8, x2 = xs[i + 1] - r - 8, mx = (x1 + x2) / 2;
        return (
          <g key={"con" + i}>
            <path className={"pl-line pl-line-" + (i + 1)} d={`M${x1} ${cy} H${x2}`} strokeWidth="2" />
            <g className={"pl-chev pl-chev-" + (i + 1)} strokeWidth="2">
              <path d={`M${mx - 6} ${cy - 6} L${mx} ${cy} L${mx - 6} ${cy + 6}`} />
              <path d={`M${mx} ${cy - 6} L${mx + 6} ${cy} L${mx} ${cy + 6}`} />
            </g>
          </g>
        );
      })}
      {stages.map((st, i) => {
        const x = xs[i];
        const isSrc = i === 0;
        const isTgt = i === tgtIdx;
        const isRel = i === stages.length - 1;
        const mod = isSrc ? " pl-node--src" : isTgt ? " pl-node--tgt" : "";
        const roleTxt = isSrc ? t("role.src").toUpperCase() : isTgt ? t("role.tgt").toUpperCase() : isRel ? t("role.rel").toUpperCase() : "";
        const roleCls = isSrc ? "pl-role pl-role--src" : isTgt ? "pl-role pl-role--tgt" : "pl-role";
        return (
          <g key={"n" + i} className={"pl-node pl-node-" + (i + 1) + mod}>
            {roleTxt && <text x={x} y={16} textAnchor="middle"><tspan className={roleCls}>{roleTxt}</tspan></text>}
            <circle className="pl-circle" cx={x} cy={cy} r={r} strokeWidth="1.5" />
            <g className="pl-glyph">
              <use href={"#" + (st.glyph || "ic-stage-dev")} x={x - 11} y={cy - 11} width={22} height={22} />
            </g>
            <text className="pl-code" x={x} y={cy + r + 18} textAnchor="middle">{st.code}</text>
            <text className="pl-name" x={x} y={cy + r + 32} textAnchor="middle">{st.name}</text>
          </g>
        );
      })}
    </svg>
  );
}
